import * as React from 'react';
import type {ComponentForms} from '@packages/systems/flows/types';
import {Page} from '@packages/systems/ui/icons';
import {UnlinkIcon} from '@packages/systems/spring';
import {
  styledDiv,
  styled,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {
  Select,
  Option,
  Group,
  Meta,
} from '@packages/systems/design-system/WebflowSelect';
import {updateExpressionAtAction} from '@packages/systems/ExpressionEditor-utils';
import {LogicLabel, useSharedFlowContext} from '@packages/systems/flows/shared';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';

import * as w from '@packages/systems/wfdl';
import * as wast from '@packages/systems/wfdl-ast';
import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';

import {TEffect} from '@packages/systems/plugins/Effect';

import {generateFormName, generateTitleFormName} from './makeFormPluginValues';
import {formDataFieldsType} from './formDataFieldsType';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const formParserType = wast.functionOf(
  formDataFieldsType,

  TEffect(wast.TTypeReference('error'), wast.TTypeReference('a'))
);

const getVariableIdentifier = ContextualExpressions.matchExpression({
  Variable: w.Variable.getName,
  _: () => null,
});

const DISCONNECT_ID = 'DISCONNECT_ID' as const;

export const FormParserType = w.TypeImplementation.Alias({
  type: formParserType,
  system: {bindUIVariant: 'custom'},
  params: ['error', 'a'],
  render: function RenderFormParser({expr, dispatch}) {
    const onChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'formId' implicitly has an 'any' type.
      (formId) => {
        const newExpression =
          // The new Form ID would be `DISCONNECT_ID` when a user is disconnecting a
          // Logic Flow from an existing connected form
          formId === DISCONNECT_ID
            ? wast.EHole()
            : w.Expressions.qualifiedVariable('__SitePlugin', formId);
        dispatch(updateExpressionAtAction(newExpression, []));
      },
      [dispatch]
    );

    const val = getVariableIdentifier(stripAnn(expr));

    return (
      <>
        <FlowTriggerSettings onChange={onChange} value={val} />
      </>
    );
  },
});

/**
 * ==================================================================
 * Form Select Menu Component
 * ==================================================================
 */
// @ts-expect-error - TS7031 - Binding element 'gutters' implicitly has an 'any' type.
const iconStyler = ({theme: {gutters}}) => ({
  width: gutters.x4 * 3,
  height: gutters.x4 * 3,
});

const PageIcon = styled(Page)(iconStyler);

const GridWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: tokens.space[2],
});

const SelectWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: tokens.space[1],
});

const HeaderIcon = styledDiv(({theme: {gutters}}) => ({
  marginRight: gutters.x4,
  width: gutters.x4 * 4,
}));

const HeaderWrapper = styledDiv(({theme: {gutters}}) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: -gutters.x4 * 5,
}));

const generateAutomationId = (
  triggerField: 'type' | 'source',
  label: string
) => {
  return `trigger-${triggerField}-option_${label}`;
};

const DisconnectWrapper = styledDiv(({theme: {gutters}}) => ({
  display: 'flex',
  columnGap: gutters.x8,
  marginLeft: -gutters.x4 * 5,
  alignItems: 'center',
}));

const DISCONNECT_LABEL = 'Disconnect form';

function FlowTriggerSettings({
  onChange,
  value,
}: {
  onChange: (arg1: string) => void;
  value: null | string;
}) {
  const {selectLogicFormFields} = useSharedFlowContext();

  const componentForms = useDesignerSelector(selectLogicFormFields);
  const options = React.useMemo(
    () => getFormSourceOptions(componentForms),
    [componentForms]
  );

  function onTriggerValChange(newSource: null | string, meta: Meta) {
    // We are only interested in the final change.
    if (meta.changeType === 'changeEnd' && newSource && newSource !== value) {
      onChange(newSource);
    }
  }

  return (
    <GridWrapper>
      <SelectWrapper>
        <LogicLabel label="Form" htmlFor="trigger-source-select" required />
        <TooltipTrigger
          alternate
          placement="left"
          content={
            options.length === 0
              ? 'To select a trigger, add a form in the Designer.'
              : ''
          }
        >
          <Select
            id="trigger-source-select"
            disabled={options.length === 0}
            value={value}
            onChange={onTriggerValChange}
            placeholder={<span>Select a form...</span>}
            aria-label={`Select a form: ${value || ''} required`}
          >
            {options.length === 0 ? (
              <Option
                // @ts-expect-error - TS2322 - Type '{ id: string | null; label: string; "data-automation-id": string; }' is not assignable to type 'IntrinsicAttributes'.
                id={value}
                label="Select a form..."
                data-automation-id={generateAutomationId('source', 'initial')}
              />
            ) : (
              <>
                <Group>
                  {options.map((option) => {
                    if (option.header) {
                      return (
                        <Option
                          key={`flow-trigger-source-header-${option.label}`}
                          header
                          value={option.label}
                          label={
                            <HeaderWrapper>
                              <HeaderIcon>{option.icon}</HeaderIcon>
                              {option.label}
                            </HeaderWrapper>
                          }
                          data-automation-id={generateAutomationId(
                            'source',
                            option.label
                          )}
                        />
                      );
                    }

                    return (
                      <Option
                        key={`flow-trigger-source-option-${option.label}`}
                        value={option.id}
                        label={option.label}
                        data-automation-id={generateAutomationId(
                          'source',
                          option.label
                        )}
                      />
                    );
                  })}
                </Group>

                {value != null && (
                  <Group hasDivider>
                    <Option
                      key={`flow-trigger-source-option-${DISCONNECT_LABEL}`}
                      value={DISCONNECT_ID}
                      label={
                        <DisconnectWrapper>
                          <UnlinkIcon size={12} />
                          <span>{DISCONNECT_LABEL}</span>
                        </DisconnectWrapper>
                      }
                      data-automation-id={generateAutomationId(
                        'source',
                        DISCONNECT_LABEL
                      )}
                    />
                  </Group>
                )}
              </>
            )}
          </Select>
        </TooltipTrigger>
      </SelectWrapper>
    </GridWrapper>
  );
}

function getFormSourceOptions(componentForms: ComponentForms) {
  const result = [];
  const namesMap = new Map();

  for (const componentId in componentForms) {
    // @ts-expect-error - TS2339 - Property 'forms' does not exist on type 'ComponentForm | undefined'. | TS2339 - Property 'title' does not exist on type 'ComponentForm | undefined'.
    const {forms, title} = componentForms[componentId];
    if (forms.length) {
      result.push({header: true, label: title, icon: <PageIcon />});
    }

    for (const form of forms) {
      const titleFormKey = generateTitleFormName(title, form.name);

      const {titleAndFormName, formName, formNames} = generateFormName({
        title,
        name: form.name,
        formNames: namesMap.get(titleFormKey) || [],
      });

      namesMap.set(titleFormKey, formNames);

      result.push({
        id: titleAndFormName,
        elementId: form.elementId,
        componentId,
        label: formName,
      });
    }
  }

  return result;
}
